import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

import {
  ActivityIndicator,
  AppState,
  Dimensions,
  Keyboard,
  Platform,
  SafeAreaView,
  StyleSheet,
  View,
} from 'react-native';
import uuid from 'react-native-uuid';

import * as Device from 'expo-device';
import * as Location from 'expo-location';
import * as Haptics from 'expo-haptics';
import * as Localization from 'expo-localization';
import * as Sentry from 'sentry-expo';
import {FontAwesome} from '@expo/vector-icons';
import Constants from 'expo-constants';
import {useTranslation} from 'react-i18next';

import EventSource from '../../utils/SourceEvent';
import {UserContext} from '../../contexts/UserContext';
import {SettingsContext} from '../../contexts/SettingsContext';
import {ThemeContext, WEB_MAX_WIDTH} from '../../contexts/ThemeContext';
import {throttle} from '../../utils/throttle';
import useSpeaker from '../../hooks/useSpeaker';
import Api from '../../constants/Api';
import StyledInput from '../../components/StyledInput';

import Chat from './Chat/Chat';
import Recorder from './Recorder';
import FooterButton, {BUTTON_MARGIN_LEFT, BUTTON_SIZE} from './FooterButton';
import StyledButton from '../../components/StyledButton';

const RTL_LANGUAGES = ['ar', 'he', 'iw', 'fa', 'dv', 'ha', 'ks', 'ku', 'ps', 'ur', 'yi'];

const DEFAULT_INPUT_HEIGHT = 19;
const FOOTER_PADDING_HORIZONTAL = 12;
const FOOTER_PADDING_VERTICAL = 8;

let shouldScroll = false;
let eventSource = null;

let backgroundTimestamp = null;

let pollingTimeout = null;
let preventPolling = false;

const ChatView = ({
  shouldntRefreshQueries,
  discussionId,
  updateDiscussionId,
  fetchDiscussions,
  querying,
  setQuerying,
}) => {
  const {t} = useTranslation();
  const {userToken, deviceToken, user, setUser} = useContext(UserContext);
  const {settings: {language}} = useContext(SettingsContext);
  const theme = useContext(ThemeContext);
  const styles = useMemo(() => getStyles(theme), [theme]);
  const speaker = useSpeaker();

  const scrollViewRef = useRef(null);

  const [canStopGenerating, setCanStopGenerating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [queries, setQueries] = useState([]);
  const [queryText, setQueryText] = useState('');

  const [examplesLoading, setExamplesLoading] = useState(false);
  const [exampleSections, setExampleSections] = useState([]);

  const [inputHeight, setInputHeight] = useState(DEFAULT_INPUT_HEIGHT);

  useEffect(() => {
    const loadExamples = async () => {
      try {
        setExamplesLoading(true);

        const response = await fetch(
          `${Api.apiBaseUrl}/${language.slice(0, 2)}/examples${Api.roomSlug ? `?room=${Api.roomSlug}` : ''}`,
          {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            },
          }
        );

        const responseJson = await response.json();

        setExampleSections(responseJson);
      } catch (error) {
        // TODO: Handle errors
        if (Platform.OS === 'web') {
          Sentry.Browser.captureException(error);
        } else {
          Sentry.Native.captureException(error);
        }
      } finally {
        setExamplesLoading(false);
      }
    };

    loadExamples();
  }, [language]);

  const fetchQueries = useCallback(async () => {
    if (discussionId) {
      setLoading(true);

      const response = await fetch(`${Api.apiBaseUrl}/discussions/${discussionId}.json?user_token=${userToken}`, {method: 'GET'});
      const data = await response.json();

      setLoading(false);

      if (data.error) {
        // TODO: handle error
        console.log(data.error);
        return;
      }

      setQueries(data.queries);

      if ((data.queries || []).length) {
        setTimeout(() => scrollViewRef && scrollViewRef.current && scrollViewRef.current.scrollToEnd({animated: false}), 0);
      }
    } else {
      setQueries([]);
    }
  }, [userToken, setLoading, setQueries, discussionId]);

  useEffect(() => {
    return () => {
      if (pollingTimeout) {
        clearTimeout(pollingTimeout);
      }
    };
  }, []);

  useEffect(() => {
    if (!shouldntRefreshQueries) {
      if (pollingTimeout) {
        clearTimeout(pollingTimeout);
      }

      fetchQueries();
    }
  }, [userToken, shouldntRefreshQueries, discussionId, fetchQueries]);

  const appState = useRef(AppState.currentState);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', nextAppState => {
      if (appState.current.match(/inactive|background/) && nextAppState === 'active') {
        // Note: App has come to the foreground

        if (backgroundTimestamp && Date.now() - backgroundTimestamp > 3 * 60 * 60 * 1000) {
          updateDiscussionId(null);
        } else {
          if (Platform.OS === 'ios') {
            fetchQueries();
          }
        }
      } else if (appState.current === 'active' && nextAppState.match(/inactive|background/)) {
        // Note: App leaves the foreground

        backgroundTimestamp = Date.now();
      }

      appState.current = nextAppState;
    });

    return () => {
      subscription.remove();
    };
  }, [fetchQueries, updateDiscussionId]);

  const pushCurrentQuery = useCallback(query => {
    preventPolling = true;

    const updatedQueries = [...(queries || []), query];

    setQueries(updatedQueries);

    return updatedQueries;
  }, [queries, setQueries]);

  const updateCurrentQuery = useCallback((queries, currentQuery) => {
    const currentQueryIndex = queries.findIndex(query => query.ref === currentQuery.ref);

    if (currentQueryIndex !== -1) {
      const updatedQueries = [...queries];
      updatedQueries[currentQueryIndex] = currentQuery;

      setQueries(updatedQueries);
    }
  }, []);

  const createQuery = useCallback(async (data, localQueries, localQuery) => {
    const fakeResponseId = uuid.v4();

    try {
      setQuerying(true);

      if (shouldScroll) {
        setTimeout(() => scrollViewRef && scrollViewRef.current && scrollViewRef.current.scrollToEnd({animated: true}), 0);
      }

      const requestObject = {
        user_token: userToken,
        device_token: deviceToken,
        room: Api.roomSlug,
        stream: true,
        query: {
          discussion_id: discussionId || undefined,
          channel: data.channel,
          ref: data.ref,
          text: data.text,
          recording_id: data.recordingId || undefined,

          country_code: Localization.getLocales()[0].regionCode,
          timezone: Localization.timezone,

          language: language, // fr-FR
          device_brand: Device.brand, // Android: "google", "xiaomi"; iOS: "Apple"; web: null
          device_name: Device.deviceName, // "Vivian's iPhone XS"
          device_manufacturer: Device.manufacturer, // Android: "Google", "xiaomi"; iOS: "Apple"; web: "Google", null
          device_os_name: Platform.OS,
          device_os_version: Device.osVersion,
          device_model_name: Device.modelName,
          expo_session_id: Constants.sessionId,
          expo_installation_id: Constants.installationId,

          screen: 'chat',
        },
      };

      const deviceTypeIndex = await Device.getDeviceTypeAsync();

      if(deviceTypeIndex === 0)       {
        requestObject.query.device_type = 'unknown';
      } else if(deviceTypeIndex === 1)  {
        requestObject.query.device_type = 'phone';
      } else if(deviceTypeIndex === 2)  {
        requestObject.query.device_type = 'tablet';
      } else if(deviceTypeIndex === 3)  {
        requestObject.query.device_type = 'desktop';
      } else if(deviceTypeIndex === 4)  {
        requestObject.query.device_type = 'tv';
      } else {
        requestObject.query.device_type = 'UNKNOWN';
      }

      let locationPerm = {};

      if (Platform.OS === 'web') {
        const {state} = await navigator.permissions.query({name: 'geolocation'});

        locationPerm = {granted: state === 'granted'};
      } else {
        locationPerm = await Location.getForegroundPermissionsAsync();
      }

      if (locationPerm.granted) {
        let location = await Location.getLastKnownPositionAsync({
          requiredAccuracy: 300, // Note: Return null if ~300 meter away from last known location
          maxAge: 600000, // Note: Return null if last known location is more than 10m old
        });

        if (!location) {
          location = await Location.getCurrentPositionAsync({
            accuracy: Location.Accuracy.High,
            mayShowUserSettingsDialog: false,
          });
        }

        requestObject.query.latitude = location.coords.latitude;
        requestObject.query.longitude = location.coords.longitude;
      }

      let appStateListener = null;

      const createRequest = new Promise((resolve, reject) => {
        eventSource = new EventSource(
          `${Api.apiBaseUrl}/queries.json`,
          {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestObject),
            // debug: true,
          }
        );

        eventSource.addEventListener('open', () => {
          console.log('Event "open" called');
          shouldScroll = true;

          speaker.initSpeakQueryState();
        });

        const throttledUpdateCurrentQuery = throttle(currentQuery => {
          updateCurrentQuery(localQueries, currentQuery);

          if (shouldScroll) {
            setTimeout(() => scrollViewRef && scrollViewRef.current && scrollViewRef.current.scrollToEnd({animated: true}), 0);
          }
        }, 300, false, false);

        let message = '';

        eventSource.addEventListener('initial', async event => {
          setCanStopGenerating(true);

          const currentQuery = JSON.parse(event.data);
          console.log('[Event "initial"] currentQuery: ', currentQuery);

          // Note: If we weren't on a discussion (eg: first use, after a clear discussion) we fetchDiscussions and
          //  update the discussionId and fetchDiscussions to update the drawer content
          if (!discussionId) {
            const fetchAndUpdateDiscussion = async () => {
              await fetchDiscussions();

              updateDiscussionId(currentQuery.discussion_id, true);
            };

            fetchAndUpdateDiscussion();
          }

          eventSource.addEventListener('message', event => {
            message += event.data;

            throttledUpdateCurrentQuery({
              ...currentQuery,
              responses: [{
                id: fakeResponseId,
                message,
                created_at: Date.now(),
                channel: currentQuery.channel,
                source: undefined,
                cards: [],
              }],
            });

            speaker.speakToken(event.data, currentQuery.language);
          });

          eventSource.addEventListener('loading_message', event => {
            // TODO:
            throttledUpdateCurrentQuery({
              ...currentQuery,
              responses: [{
                id: fakeResponseId,
                message: event.data,
                created_at: Date.now(),
                channel: currentQuery.channel,
                source: undefined,
                cards: [],
              }],
            });
          });

          if (Platform.OS === 'ios') {
            appStateListener = AppState.addEventListener('change', nextAppState => {
              appStateListener.remove();

              if (nextAppState === 'inactive' || nextAppState === 'background') {
                if (eventSource) {
                  eventSource.removeAllEventListeners();
                  eventSource.close();
                  eventSource = null;
                }

                preventPolling = false;
                setQuerying(false);
                setCanStopGenerating(false);
              }
            });
          }
        });

        eventSource.addEventListener('final', async event => {
          eventSource.removeAllEventListeners();
          eventSource.close();
          eventSource = null;
          preventPolling = false;
          setQuerying(false);
          setCanStopGenerating(false);

          const query = JSON.parse(event.data);
          console.log('Event "final": ', query);

          if (query.responses && query.responses.length && query.responses[0].message) {
            speaker.emptyResponseBuffer(query.responses[0].message, query.responses[0].language);
          }

          const newQueries = [...queries, query];

          setQueries(newQueries);

          if (query.status === 'pending') {
            const pollPendingQueries = async () => {
              pollingTimeout = null;

              if (preventPolling) {
                // Note: If preventPolling is true we delay the next request
                pollingTimeout = setTimeout(pollPendingQueries, 5000);
              } else {
                const response = await fetch(`${Api.apiBaseUrl}/discussions/${query.discussion_id}.json?user_token=${userToken}`, {method: 'GET'});
                const updatedDiscussion = await response.json();

                const updatedQueries = (updatedDiscussion || {}).queries || [];

                if (updatedQueries.some(fetchedQuery => fetchedQuery.status === 'pending')) {
                  pollingTimeout = setTimeout(pollPendingQueries, 5000);
                }

                setQueries(updatedQueries);
              }
            };

            pollingTimeout = setTimeout(pollPendingQueries, 5000);
          }

          const updatedUser = {...user, daily_queries: query.daily_queries};

          setUser(updatedUser);

          resolve(query);
        });

        eventSource.addEventListener('error', (event) => {
          // TODO: Handle errors
          if (event.type === 'error') {
            console.log('Connection error:', event.message);
          } else if (event.type === 'exception') {
            console.log('Error:', event.message, event.error);
          }

          updateCurrentQuery(localQueries, {
            ...localQuery,
            responses: [{
              id: fakeResponseId,
              message: t('MainScreen.ChatView.Recorder.errors.recordingFailed', 'Sorry, something went wrong, try again later.'),
              status: 'error',
              created_at: Date.now(),
              channel: localQuery.channel,
              source: undefined,
              cards: [],
            }],
          });

          eventSource.removeAllEventListeners();
          eventSource.close();
          eventSource = null;
          preventPolling = false;
          setQuerying(false);
          setCanStopGenerating(false);
          reject({message: event.message});
        });

        eventSource.addEventListener('close', () => {
          eventSource.removeAllEventListeners();
          eventSource.close();
          eventSource = null;
          preventPolling = false;
          setQuerying(false);
          setCanStopGenerating(false);
        });
      });

      const result = await createRequest;

      if (appStateListener) {
        appStateListener.remove();
      }

      console.log('createRequest() result', result);

      return result;
    } catch (error) {
      // TODO: Handle errors
      if (Platform.OS === 'web') {
        Sentry.Browser.captureException(error);
      } else {
        Sentry.Native.captureException(error);
      }

      if (eventSource) {
        eventSource.removeAllEventListeners();
        eventSource.close();
        eventSource = null;
      }

      preventPolling = false;
      setQuerying(false);
      setCanStopGenerating(false);

      updateCurrentQuery(localQueries, {
        ...localQuery,
        responses: [{
          id: fakeResponseId,
          message: t('MainScreen.ChatView.Recorder.errors.recordingFailed', 'Sorry, something went wrong, try again later.'),
          status: 'error',
          created_at: Date.now(),
          channel: localQuery.channel,
          source: undefined,
          cards: [],
        }],
      });
    }
  }, [setQuerying, userToken, deviceToken, discussionId, language, speaker, updateCurrentQuery, fetchDiscussions, updateDiscussionId, queries, user, setUser, t]);

  const handleSendQuery = useCallback(async text => {
    if (
      !text.length
      || !text.trim().length
      // Note: THIS IS NOT A SIMPLE SPACE, DO NOT DELETE !
      //  This is a weird character that is added when on iPhone someone use the voice control
      //  but doesn't speak.
      || text === '￼'
    ) {
      if (Platform.OS !== 'web') {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
      }

      return;
    }

    setQueryText('');
    setInputHeight(DEFAULT_INPUT_HEIGHT);
    Keyboard.dismiss();

    const ref = uuid.v4();

    const localQuery = {
      ref,
      status: 'processing',
      text: text.trim(),
      channel: 'keyboard',
      created_at: new Date(),
      updated_at: new Date(),
      responses: [],
    };

    // Note: Optimistically add a fake query until the server return the result
    const localQueries = pushCurrentQuery(localQuery);

    setTimeout(() => scrollViewRef && scrollViewRef.current && scrollViewRef.current.scrollToEnd({animated: true}), 0);

    await createQuery({ref, channel: 'keyboard', text: text.trim()}, localQueries, localQuery);

    setTimeout(() => scrollViewRef && scrollViewRef.current && scrollViewRef.current.scrollToEnd({animated: true}), 0);
  }, [pushCurrentQuery, createQuery]);

  const handleReprocessQuery = useCallback(async (query, update) => {
    preventPolling = true;

    const fakeResponseId = uuid.v4();

    const optimisticallyUpdatedQuery = {
      ...query,
      responses: [
        ...query.responses,
        {
          id: fakeResponseId,
          created_at: Date.now(),
          channel: query.channel,
          source: undefined,
          cards: [],
        },
      ],
    };

    const queryIndex = queries.findIndex(query => query.ref === optimisticallyUpdatedQuery.ref);

    if (queryIndex !== -1) {
      const updatedQueries = [...queries];
      updatedQueries[queryIndex] = optimisticallyUpdatedQuery;

      setQueries(updatedQueries);
    }

    setTimeout(() => scrollViewRef && scrollViewRef.current && scrollViewRef.current.scrollToEnd({animated: true}), 0);

    const result = await fetch(`${Api.apiBaseUrl}/queries/${query.id}/reprocess`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_token: userToken,
        query: update,
      }),
    });

    const updatedQuery = await result.json();

    if (queryIndex !== -1) {
      const updatedQueries = [...queries];
      updatedQueries[queryIndex] = updatedQuery;

      setQueries(updatedQueries);
    }

    setTimeout(() => scrollViewRef && scrollViewRef.current && scrollViewRef.current.scrollToEnd({animated: true}), 0);

    preventPolling = false;
  }, [queries, userToken]);

  const onChangeText = useCallback(value => setQueryText(value), [setQueryText]);
  const onSubmitEditing = useCallback(event => handleSendQuery(event.nativeEvent.text), [handleSendQuery]);

  // Note: onContentSize doesn't work properly on web, check: https://github.com/necolas/react-native-web/issues/2160
  const onChangeWeb = useCallback(event => {
    if (Platform.OS !== 'web') return;

    window.requestAnimationFrame(() => {
      event.nativeEvent.target.style.height = '0px';

      const height = event.nativeEvent.target.scrollHeight < DEFAULT_INPUT_HEIGHT ? DEFAULT_INPUT_HEIGHT : Math.min(event.nativeEvent.target.scrollHeight, 120);

      event.nativeEvent.target.style.height = `${height}px`;

      setInputHeight(height < DEFAULT_INPUT_HEIGHT ? DEFAULT_INPUT_HEIGHT : Math.min(height, 120));
    });
  }, []);

  const onKeyPress = useCallback(event => {
    if (Platform.OS === 'web') {
      const {nativeEvent: {key, shiftKey}} = event;

      if (key === 'Enter' && !shiftKey) {
        handleSendQuery(queryText);
      }
    }
  }, [handleSendQuery, queryText]);

  const onContentSizeChange = useCallback(event => {
    const {nativeEvent: {contentSize: {height}}} = event;

    setInputHeight(height < DEFAULT_INPUT_HEIGHT ? DEFAULT_INPUT_HEIGHT : Math.min(height, 120));
  }, [setInputHeight]);

  return (
    <SafeAreaView style={{flex:1, width: '100%', backgroundColor: theme.headerBackground}}>
      <View
        style={{
          flex: 1,
          width: '100%',
          backgroundColor: theme.backgroundPrimary,
          paddingBottom: Platform.OS === 'web' ? inputHeight + 24 + (2 * FOOTER_PADDING_VERTICAL) : undefined,
        }}
      >
        {loading || examplesLoading
          ? (
            <View style={{flex: 1, justifyContent: 'center'}}>
              <ActivityIndicator size="large"/>
            </View>
          )
          : (
            <Chat
              scrollViewRef={scrollViewRef}
              onScrollBeginDrag={() => {
                shouldScroll = false;
              }}
              queries={queries}
              setQueryText={setQueryText}
              handleReprocessQuery={handleReprocessQuery}
              exampleSections={exampleSections}
              canStopGenerating={canStopGenerating}
            />
          )}

        {canStopGenerating && (
          <View style={{position: 'relative', alignItems: 'center'}}>
            <StyledButton
              style={{position: 'absolute', backgroundColor: theme.backgroundPrimary, bottom: 8, width: 200}}
              text="■ Stop generating"
              variant="outlined"
              onPress={() => {
                if (eventSource) {
                  eventSource.close();
                  eventSource = null;
                }

                const updatedQueries = [...queries];
                const currentQuery = queries[queries.length - 1];

                updatedQueries[updatedQueries.length - 1] = {
                  ...currentQuery,
                  responses: currentQuery.responses.length
                    ? currentQuery.responses.map(response => ({
                      ...response,
                      // TODO: Translate
                      message: `${response.message} [canceled]`,
                    }))
                    : [{
                      id: uuid.v4(),
                      // TODO: Translate
                      message: '[canceled]',
                      created_at: Date.now(),
                      channel: currentQuery.channel,
                      source: undefined,
                      cards: [],
                    }],
                };

                setQueries(updatedQueries);
                preventPolling = false;
                setQuerying(false);
              }}
            />
          </View>
        )}

        <View style={[styles.footer, {height: inputHeight + 24 + (2 * FOOTER_PADDING_VERTICAL)}]}>
          <View style={styles.innerFooter}>
            <StyledInput
              style={{
                height: inputHeight + 24,
                width: Math.min(Dimensions.get('window').width, 800) - 2 * FOOTER_PADDING_HORIZONTAL - BUTTON_SIZE - BUTTON_MARGIN_LEFT,
              }}
              inputStyle={{
                height: inputHeight,
                lineHeight: 16,
                textAlign: RTL_LANGUAGES.includes(language.slice(0, 2)) ? 'right' : 'left',
              }}
              onChangeText={onChangeText}
              // Note: onContentSize doesn't work properly on web, check: https://github.com/necolas/react-native-web/issues/2160
              onChange={Platform.OS === 'web' ? onChangeWeb: undefined}
              onContentSizeChange={Platform.OS === 'web' ? undefined : onContentSizeChange}
              onKeyPress={onKeyPress}
              onSubmitEditing={onSubmitEditing}
              value={queryText}
              placeholder={t('MainScreen.ChatView.input.placeholder', 'Type anything...')}
              multiline
            />

            {queryText.length || (Platform.OS === 'web' && !window.MediaRecorder.isTypeSupported('audio/webm'))
              ? (
                <FooterButton
                  disabled={querying}
                  onPress={() => handleSendQuery(queryText)}
                  iconBackgroundColor={theme.colorPrimary}
                  icon={<FontAwesome name="send" size={22} color="white"/>}
                />
              )
              : (
                <Recorder
                  disabled={querying}
                  pushLocalQuery={pushCurrentQuery}
                  updateCurrentQuery={updateCurrentQuery}
                  createQuery={createQuery}
                  scrollViewRef={scrollViewRef}
                />
              )
            }
          </View>
        </View>
      </View>
    </SafeAreaView>
  );
};

const getStyles = theme => StyleSheet.create({
  footer: {
    ...(Platform.OS === 'web'
      ? {
        position: 'fixed',
        bottom: 0,
      }
      : {}),
    backgroundColor: theme.footerBackground,
    alignItems: 'center',
    width: '100%',
    borderTopColor: theme.footerBorderColor,
    borderTopWidth: 1,
    borderTopStyle: 'solid',
  },
  innerFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: WEB_MAX_WIDTH,
    width: '100%',
    height: '100%',
    justifyContent: 'flex-start',
    paddingLeft: FOOTER_PADDING_HORIZONTAL,
  },
});

export default ChatView;
